import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import ProgressIndicator from 'components/common/progress-indicator';
import Tooltip from 'components/layout/tooltip';
import { ReactNode } from 'react';

type LevelAccordionItemProps = Readonly<{
  label: string;
  expanded: boolean;
  indicatorChecked: boolean;
  indicatorRequired?: boolean;
  deleteDisabled?: boolean;
  onToggle: () => void;
  onDelete: () => void;
  children: ReactNode;
}>;

export default function LevelAccordionItem({
  label,
  expanded,
  indicatorChecked,
  indicatorRequired,
  deleteDisabled,
  onToggle,
  onDelete,
  children
}: LevelAccordionItemProps) {
  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      <div className='flex w-full items-center hover:bg-gray-50'>
        <button
          type='button'
          onClick={onToggle}
          className='flex min-w-0 flex-1 items-center justify-between gap-2 py-3 pl-4 pr-1 text-left'
        >
          <span className='flex items-center gap-2 font-medium text-gray-800'>
            <ProgressIndicator
              checked={indicatorChecked}
              required={indicatorRequired}
            />
            {label}
          </span>
          {expanded ? (
            <ChevronUpIcon className='h-5 w-5 text-gray-500' />
          ) : (
            <ChevronDownIcon className='h-5 w-5 text-gray-500' />
          )}
        </button>
        <button
          type='button'
          aria-disabled={deleteDisabled}
          onClick={() => {
            if (!deleteDisabled) {
              onDelete();
            }
          }}
          className={clsx(
            'relative mr-2 px-2 py-3',
            deleteDisabled
              ? 'cursor-not-allowed text-gray-300'
              : 'text-gray-500 hover:text-red-500'
          )}
        >
          <TrashIcon className='h-5 w-5' />
          <Tooltip
            text={
              deleteDisabled
                ? 'A structure needs at least one level'
                : 'Delete Level'
            }
          />
        </button>
      </div>
      <div
        className={clsx(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className='overflow-hidden'>
          <div className='border-t border-gray-200 p-3'>{children}</div>
        </div>
      </div>
    </div>
  );
}
